import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, UploadFileOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { ReactElement } from "react"
import { useState } from "react"

import { ProductFileTypeToLabel } from "../constants"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import type { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { AddProductFileDialog } from "@/screens/Products/components/Product/Information/ProductFiles/AddProductFileDialog"
import { ProductFileTable } from "@/screens/Products/components/Product/Information/ProductFiles/ProductFileTable/ProductFileTable"

export const ProductFilesContainer = (): ReactElement => {
  const { productId } = useParams({ from: "/app/product/$productId/edit/_layout/information" })
  const [expanded, setExpanded] = useState(true)
  const [addFileDialogOpen, setAddFileDialogOpen] = useState(false)

  const { fileList, fileFetching, refetchFileUploadData } = useGetFileUploadData({
    referenceId: `${productId}`,
    fieldGroups: Object.keys(ProductFileTypeToLabel).map((val) => val as unknown as FieldGroupEnum),
    referenceTableName: ReferenceTableName.Product,
  })
  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  return (
    <>
      {addFileDialogOpen && (
        <AddProductFileDialog
          open={addFileDialogOpen}
          onClose={() => setAddFileDialogOpen(false)}
          onRefresh={refetchFileUploadData}
          productId={productId}
        />
      )}
      <Paper className='border border-gray-300 p-6' elevation={0} data-testid='product-files-container'>
        <Box className='flex items-center justify-between'>
          <Typography variant='h6' data-testid='product-files-title'>
            Uploaded Files
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
        <Collapse in={expanded} className='mt-6'>
          {fileList?.length ? (
            <Box className='flex flex-col'>
              <Button
                color='primary'
                size='small'
                appearance='outlined'
                className='mb-4 self-end text-primary-400'
                startIcon={<UploadFileOutlined />}
                onClick={() => setAddFileDialogOpen(true)}
                data-testid='add-file-button'
              >
                Upload Files
              </Button>
              <ProductFileTable data={fileList} isLoading={fileFetching} onRemove={removeSingleFile} />
            </Box>
          ) : (
            <InformationMissingAlert
              title='Upload Files'
              action={
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<UploadFileOutlined />}
                  onClick={() => setAddFileDialogOpen(true)}
                  data-testid='add-file-button'
                >
                  Upload Files
                </Button>
              }
              description='PDF, PNG, JPG or DOC (max. 10MB)'
              data-testid='product-file-alert'
            />
          )}
        </Collapse>
      </Paper>
    </>
  )
}
